import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  OnInit,
  Renderer2,
  OnDestroy,
} from "@angular/core";
import { CoreService } from "@assoanalytics/app/core/services/core.service";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Router, RouterModule } from "@angular/router";
import { MaterialModule } from "@assoanalytics/app/material.module";
import { NgScrollbarModule } from "ngx-scrollbar";
import { BrandingComponent } from "../sidebar/branding.component";
import { DatePipe } from "@angular/common";
import { AuthService } from "@assoanalytics/app/core/services/auth.service";
import { BehaviorSubject, merge, Subscription, switchMap } from "rxjs";
import { User } from "@assoanalytics/app/core/models/user/user";
import { UserRole } from "@assoanalytics/app/core/models/user/user-role";
import { AlertDialogService } from "@assoanalytics/app/shared/services/alert-dialog/alert-dialog.service";
import { SharedModule } from "@assoanalytics/app/shared/shared.module";
import { defaults } from "@assoanalytics/app/app.config";
import { AclService } from "@assoanalytics/app/core/acl/acl.service";
import { ResponsiveService } from "@assoanalytics/app/core/services/responsive.service";
import { provideTablerIcons, TablerIconComponent } from "angular-tabler-icons";
import {
  IconKey,
  IconLogout,
  IconMenu2,
  IconShieldLock,
  IconSteeringWheel,
  IconUser,
  IconUserPlus,
  IconZoomIn,
  IconZoomOut,
} from "angular-tabler-icons/icons";
import { UserDataService } from "@assoanalytics/app/core/services/api/user-data/user-data.service";
import { NavService } from "@assoanalytics/app/core/services/nav.service";
import { TokenService } from "@assoanalytics/app/core/services/token.service";
import { LanguageSwitcherComponent } from "@assoanalytics/app/shared/components/language-switcher/language-switcher.component";

@Component({
  selector: "app-header",
  imports: [
    RouterModule,
    SharedModule,
    NgScrollbarModule,
    MaterialModule,
    BrandingComponent,
    TablerIconComponent,
  ],
  providers: [
    provideTablerIcons({
      IconKey,
      IconUser,
      IconUserPlus,
      IconLogout,
      IconMenu2,
      IconZoomIn,
      IconZoomOut,
      IconShieldLock,
      IconSteeringWheel,
    }),
  ],
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Input() role: UserRole;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  loading = new BehaviorSubject<boolean>(true);
  showFiller = false;
  username: string | null;
  user: User | null;
  htmlDescription: string;
  parsedDescription: string | null;

  userRoles = UserRole;

  supportedLanguages = defaults.supportedLanguages;

  showLanguageSwitcher: boolean = false;

  currentLanguage: string;

  // Aggiunta proprietà per gestire il percorso dell'immagine della bandiera
  flagImgSrc: string;

  mfaEnabled = false;

  protected isMobile: boolean = false;

  private zoomLevel: number = 1;

  constructor(
    public dialog: MatDialog,
    private authService: AuthService,
    private router: Router,
    private datePipe: DatePipe,
    private alertDialogService: AlertDialogService,
    private translateService: TranslateService,
    private aclService: AclService,
    private responsiveService: ResponsiveService,
    private userDataService: UserDataService,
    private navService: NavService,
    private tokenService: TokenService,
  ) {
    this.currentLanguage = this.translateService.currentLang;

    // Imposta il percorso iniziale della bandiera
    this.flagImgSrc = `/assets/images/flag/icon-flag-${this.currentLanguage}.svg`;

    const languagesNumber = Object.keys(this.supportedLanguages).length;
    if (languagesNumber > 1) {
      this.showLanguageSwitcher = true;
    } else {
      this.showLanguageSwitcher = false;
    }

    this.responsiveService.getCurrentBreakpoint().subscribe((breakpoint) => {
      this.isMobile = breakpoint === "XSmall";
    });

    this.userDataService.currentUserAuthData.subscribe((userAuthData) => {
      if (userAuthData) {
        this.mfaEnabled = userAuthData.strong_auth;
      }
    });
  }

  ngOnInit() {
    this.user = this.authService.getCurrentUser();

    if (this.user) {
      this.username = this.user.username;
      this.role = this.user.role;
    }
  }

  onDemandLanguageSwitch() {
    this.alertDialogService.openModal(LanguageSwitcherComponent);
  }

  logout() {
    this.authService.logout();
    this.router.navigate(["/login"]);
  }

  enabledForRole(module: string, component: string) {
    return this.aclService.isChildEnabled(module, component);
  }

  parseDescription(description: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(description, "text/html");
    if (doc.body.textContent) {
      return doc.body.textContent;
    }
    return "";
  }

  formatNewsDate(newsDate: string) {
    const date = new Date(newsDate);
    const now = new Date();

    const timeDifference = now.getTime() - date.getTime();

    const minutesDifference = Math.floor(timeDifference / (1000 * 60));

    if (minutesDifference < 60) {
      if (minutesDifference <= 1) {
        return "1 minuto fa";
      } else {
        return minutesDifference + " minuti fa";
      }
    } else if (minutesDifference >= 60 && minutesDifference < 1440) {
      const hoursDifference = Math.floor(minutesDifference / 60);
      return hoursDifference + " ore fa";
    } else {
      return this.datePipe.transform(date, "dd/MM/yyyy");
    }
  }

  increaseZoom() {
    if (this.zoomLevel < 2) {
      this.zoomLevel += 0.1;
      document.body.style.zoom = this.zoomLevel.toString();
    }
  }

  decreaseZoom() {
    if (this.zoomLevel > 0.5) {
      this.zoomLevel -= 0.1;
      document.body.style.zoom = this.zoomLevel.toString();
    }
  }

  // Metodo per gestire l'errore di caricamento dell'immagine della bandiera
  onFlagImgError() {
    this.flagImgSrc = "/assets/images/flag/icon-flag-default.svg";
  }

  setCurrentLanguage(lang: string) {
    this.currentLanguage = lang;
    this.flagImgSrc = `/assets/images/flag/icon-flag-${lang}.svg`;
  }

  isMasterUser(): boolean {
    return this.authService.isMaster();
  }

  messageCookiesEnabled(): boolean {
    // Ottieni data emissione token (iat)
    const tokenData = this.tokenService.getTokenData();
    if (!tokenData?.iat) return false;
    const tokenIat = tokenData.iat * 1000; // iat in secondi -> ms

    // Controllo cookie e scadenza
    const cookieName = "message_alert";
    const cookies = document.cookie.split(";").map((c) => c.trim());
    const found = cookies.find((c) => c.startsWith(cookieName + "="));
    let cookieValid = false;
    let cookieSavedCount = 0;
    if (found) {
      const value = Number(found.split("=")[1]);
      cookieSavedCount = value;
      if (value >= 1) {
        // Controlla la scadenza: cerca la data di scadenza nel cookie (se presente)
        // ngx-cookie-service salva la scadenza come attributo expires, ma document.cookie non la espone direttamente.
        // Quindi: per sicurezza, se il cookie esiste e vale 1, verifica la scadenza confrontando la data attuale (tokenIat) con la data di scadenza stimata (1 mese da iat)
        // Se il cookie è stato impostato a partire da iat, la scadenza è iat + 1 mese
        const cookieSetDate = new Date(tokenIat); // iat = data emissione token
        const cookieExpireDate = new Date(cookieSetDate);
        cookieExpireDate.setMonth(cookieExpireDate.getMonth() + 1);
        if (cookieExpireDate > new Date(tokenIat)) {
          // Cookie ancora valido rispetto a iat
          cookieValid = true;
        }
      }
    }

    // Se cookie non trovato, valore maggiore di messagesCount, o scaduto, mostro alert nuovi messaggi
    if (!cookieValid) {
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {}
}
